// src/oura/merchant/HomeTop.tsx
//
// Home's first row under the redesign (19 Oct 2026): the one thing that needs
// the owner, beside everything from their account manager. Side by side from
// 1024px, the account manager taking two thirds; stacked on a phone, the
// thing that needs them first.
//
// "Needs you" is the head of the one queue (docAsks.ts), so it agrees with To
// do and the rail's badge. The account manager card carries their latest
// weekly note and what the owner asked them; "Ask for a change" is a button
// that opens the form in a pop-up, so the form is not on the page all the
// time (Jack, 18 Oct 2026) and opening it never stretches the row beside it.

import { useState } from "react";
import { CheckIcon, FlagIcon, ImageIcon, PlusIcon } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@component/dialog";
import { canRequestChange, malaysianMonthNow, type ClientPagePayload, type Thread } from "@service/aeo/clientReview";
import { ArrowLink, PillButton, PillLink, SURFACE, Tag, TYPE } from "@oura/ui";
import { cn } from "../lib/cn";
import { picturesWord } from "../components/ReplyPictures";
import { dayDate, initials } from "../lib/oura";
import { OURA_ROOT } from "../nav";
import { useMyChangeRequests } from "../hooks/useMyChangeRequests";
import { askFact } from "./asks";
import { docDue, isEntryLate, type QueueEntry } from "./docAsks";
import { ChangeRequestDrawer } from "./drawers/ChangeRequestDrawer";
import { myRequestGroup, myRequestRows, recentlyDone, type MyRequestRow } from "./myRequests";

/** Requests the card names; Our work has the rest. */
const REQUESTS_SHOWN = 2;

/** The button names the job when the title is short enough to be one ("Send your prices"). */
const JOB_LABEL_MAX = 28;
function jobLabel(entry: QueueEntry): string {
    if (entry.kind !== "question") return "Open";
    const title = entry.title.trim();
    return title.length > 0 && title.length <= JOB_LABEL_MAX ? title : "Answer";
}

/** The head of the queue, with one button that opens it there. */
export function NeedsYouCard({
    queue,
    threadOf,
    onOpen,
    planWaiting = 0,
    setupLeft = 0,
}: {
    queue: QueueEntry[];
    threadOf: (itemId: string) => Thread | null;
    onOpen: (id: string) => void;
    /** Plan lines waiting on the owner (a small change to name, an access to give): not questions, still theirs. */
    planWaiting?: number;
    /**
     * Set-up steps still theirs. Counted into "1 of N" and "See all N", so the
     * card says the same number as the To do badge, which counts what To do
     * lists: set-up steps and the asks (Jack, 22 Oct 2026: 10 on the menu, 9 here).
     */
    setupLeft?: number;
}) {
    const first = queue[0];
    const listed = queue.length + setupLeft;
    if (!first && planWaiting > 0) {
        // Nothing to answer, but work waits on them: never "up to date" beside the month card's "need you" (#4388).
        return (
            <section aria-label="Needs you" className={cn(SURFACE.card, "flex flex-col gap-2.5 p-6")}>
                <span aria-hidden className="grid h-11 w-11 place-items-center rounded-xl bg-blue-50 text-blue-600">
                    <FlagIcon size={20} strokeWidth={2} />
                </span>
                <h2 className={cn(TYPE.tt, "m-0 mt-1.5 text-gray-900")}>Nothing to answer right now</h2>
                <p className={cn(TYPE.tb, "m-0 text-gray-500")}>
                    {planWaiting === 1 ? "1 piece of work waits on you." : `${planWaiting} pieces of work wait on you.`}
                </p>
                <p className="m-0 mt-auto pt-1">
                    <ArrowLink to={`${OURA_ROOT}/work`}>See what is waiting</ArrowLink>
                </p>
            </section>
        );
    }
    if (!first) {
        return (
            <section aria-label="Needs you" className={cn(SURFACE.card, "flex flex-col gap-2.5 p-6")}>
                <span aria-hidden className="grid h-11 w-11 place-items-center rounded-xl bg-emerald-50 text-emerald-700">
                    <CheckIcon size={20} strokeWidth={2} />
                </span>
                <h2 className={cn(TYPE.tt, "m-0 mt-1.5 text-gray-900")}>You are up to date</h2>
                <p className={cn(TYPE.tb, "m-0 text-gray-500")}>When we need an answer or a document from you, it shows here first.</p>
            </section>
        );
    }
    const fact = first.kind === "question" ? askFact(first.ask, threadOf(first.id)) : docDue(first.doc);
    const why = first.kind === "question" ? first.ask.item.why : "A document for you to read and reply to.";
    return (
        <section aria-label="Needs you" className={cn(SURFACE.card, "flex flex-col gap-2.5 p-6")}>
            <div className="flex items-center gap-3">
                <span aria-hidden className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-amber-50 text-amber-700">
                    <FlagIcon size={20} strokeWidth={1.75} />
                </span>
                <div className="flex min-w-0 flex-col items-start gap-0.5">
                    {/* Amber, as MERCHANT_STATUS says it everywhere: in blue it read as one of our "In progress" items (audit, 24 Oct). */}
                    <Tag tone="amber">Needs you</Tag>
                    <span className={TYPE.lab}>{listed === 1 ? "The only thing on your list" : `1 of ${listed} on your list`}</span>
                </div>
            </div>
            <h2 className={cn(TYPE.tt, "m-0 mt-1.5 text-gray-900")}>{first.title}</h2>
            {why && <p className={cn(TYPE.tb, "m-0 line-clamp-3 text-gray-500")}>{why}</p>}
            {/* Past its date is said plainly, in grey: the amber tag already says it is theirs. */}
            {fact && <p className={cn(TYPE.tc, "m-0", isEntryLate(first) || fact.late ? "font-medium text-gray-700" : "text-gray-500")}>{fact.text.replace(/^Was due /, "Due ")}</p>}
            <div className="mt-auto flex flex-wrap gap-2 pt-2">
                {/* One blue press per screen, and on Home it is this one: the thing
                    that needs the owner, named for the job ("Send your prices"),
                    while the setup card steps down to white (audit, 24 Oct 2026). */}
                <PillButton variant="primary" onClick={() => onOpen(first.id)}>
                    {jobLabel(first)}
                </PillButton>
                {listed > 1 && (
                    <PillLink to={`${OURA_ROOT}/decisions`}>See all {listed}</PillLink>
                )}
            </div>
        </section>
    );
}

/** The account manager: who they are, their latest note, what you asked them, and the way to ask. */
export function ManagerCard({ payload }: { payload: ClientPagePayload }) {
    const [asking, setAsking] = useState(false);
    const canAsk = canRequestChange() && !payload.previewedByStaff;
    const { read, refresh } = useMyChangeRequests(canAsk);
    const rows = read.state === "ready" ? myRequestRows(read.requests, read.calls) : [];
    const open = rows.filter((r) => myRequestGroup(r) === "with_us");
    // Open first, then the ones done this week with the reply that closed them
    // (#4718). Each group has its own limit, so open requests never push a
    // finished one, and its reply, off Home.
    const shown = [...open.slice(0, REQUESTS_SHOWN), ...recentlyDone(rows).slice(0, REQUESTS_SHOWN)];
    const manager = payload.preparedBy?.trim() || null;
    const email = payload.contactEmail?.trim() || null;
    const note = payload.weekNote ?? null;
    const firstName = manager?.split(/\s+/)[0] ?? null;
    const askLabel = firstName ? `Ask ${firstName} for a change` : "Ask for a change";
    // "1 small change a month" is said only where the contract carries it (G5).
    const smallChangeInPlan = payload.engagement?.deliverables.some((d) => d.code === "G5") ?? false;
    const smallChangeUsedAt =
        read.state === "ready"
            ? read.requests
                  .filter((r) => r.smallChange?.month === malaysianMonthNow())
                  .map((r) => r.at)
                  .sort()[0] ?? null
            : null;

    return (
        <section aria-labelledby="home-manager" className={cn(SURFACE.card, "flex flex-col gap-5 p-6 lg:col-span-2")}>
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
                <div className="flex min-w-0 items-center gap-3">
                    <span aria-hidden className={cn(TYPE.tt, "grid h-11 w-11 shrink-0 place-items-center rounded-full bg-blue-50 text-blue-700")}>
                        {manager ? initials(manager) : "o"}
                    </span>
                    <div className="min-w-0">
                        <h2 id="home-manager" className={cn(TYPE.tt, "m-0 text-gray-900")}>
                            From your account manager
                        </h2>
                        {/* Nobody assigned yet (the server sends null, #4808): ourteam, as everywhere else. */}
                        <p className={cn(TYPE.tc, "m-0 break-words text-gray-500")}>{[manager ?? "ourteam", email].filter(Boolean).join(" · ")}</p>
                    </div>
                </div>
                {canAsk && (
                    <PillButton icon={PlusIcon} writes onClick={() => setAsking(true)}>
                        {askLabel}
                    </PillButton>
                )}
            </div>
            <div className="grid gap-x-6 gap-y-4 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
                <div className="self-start rounded-xl bg-gray-50 px-4 py-3.5">
                    {note ? (
                        <>
                            {/* "Weekly note", dated by the note itself: the server sends the newest however old it is. */}
                            <p className={cn(TYPE.lab, "m-0")}>Weekly note{dayDate(note.date) ? ` · ${dayDate(note.date)}` : ""}</p>
                            <p className={cn(TYPE.tb, "m-0 mt-1.5 whitespace-pre-line leading-[22px] text-gray-900")}>{note.body}</p>
                        </>
                    ) : (
                        <p className={cn(TYPE.tb, "m-0 text-gray-500")}>No note yet. {manager ?? "Your account manager"} writes one as the work moves.</p>
                    )}
                </div>
                {read.state !== "notServed" && <Requests read={read.state} shown={shown} total={rows.length} manager={manager} />}
            </div>
            {canAsk && (
                <Dialog
                    open={asking}
                    onOpenChange={(next) => {
                        setAsking(next);
                        if (!next) void refresh();
                    }}
                >
                    <DialogContent className="max-w-xl gap-5 rounded-2xl border-0 p-6 sm:rounded-2xl" closeButtonClassName="right-5 top-5">
                        <div className="pr-8">
                            <DialogTitle className={cn(TYPE.tt, "m-0 text-gray-900")}>{askLabel}</DialogTitle>
                            <DialogDescription className={cn(TYPE.tc, "m-0 mt-0.5 break-words text-gray-500")}>
                                {[manager ? `To ${manager}` : "To your account manager", email].filter(Boolean).join(" · ")}
                            </DialogDescription>
                        </div>
                        <ChangeRequestDrawer
                            inline
                            open
                            about={null}
                            onClose={() => {
                                setAsking(false);
                                void refresh();
                            }}
                            businessId={payload.business.id}
                            manager={payload.preparedBy}
                            email={payload.contactEmail ?? null}
                            viewer={payload.viewer.name}
                            smallChangeInPlan={smallChangeInPlan}
                            smallChangeUsedAt={smallChangeUsedAt}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </section>
    );
}

/**
 * What the owner asked: what is still with us, then what we finished this week,
 * each with our reply under it. Our work holds every request, so the link to it
 * shows whenever there is one (#4718): with Our work gone from the menu, a done
 * request and its reply were otherwise out of reach.
 */
function Requests({ read, shown, total, manager }: { read: "loading" | "ready" | "failed"; shown: MyRequestRow[]; total: number; manager: string | null }) {
    return (
        <div className="min-w-0 pt-1">
            <p className={cn(TYPE.lab, "m-0 mb-2")}>Your requests</p>
            {read === "loading" ? (
                <span className="block h-12 animate-pulse rounded-xl bg-gray-100" />
            ) : read === "failed" ? (
                <p className={cn(TYPE.tc, "m-0 text-gray-500")}>Your requests could not be loaded. They are on Our work.</p>
            ) : shown.length === 0 ? (
                <p className={cn(TYPE.tc, "m-0 text-gray-500")}>Nothing open. Anything you ask {manager ?? "us"} shows here with the reply.</p>
            ) : (
                <ul className="list-plain m-0 space-y-4 p-0">
                    {shown.map((r) => {
                        const done = myRequestGroup(r) === "done";
                        return (
                            <li key={`${r.kind}:${r.id}`} className="flex flex-col items-start gap-1">
                                <span className={cn(TYPE.tb, "line-clamp-2 font-medium text-gray-900")}>
                                    {r.kind === "change" ? r.change.note : r.call.about ? `A call about ${r.call.about}` : "A call"}
                                </span>
                                {r.kind === "change" && r.change.reply && (
                                    <span className={cn(TYPE.tc, "line-clamp-2 text-gray-500")}>
                                        {r.change.reply.by || manager || "We"}: {r.change.reply.body}
                                    </span>
                                )}
                                {r.kind === "change" && r.change.reply && r.change.reply.files.length > 0 && (
                                    <span className={cn(TYPE.tc, "inline-flex items-center gap-1 text-gray-500")}>
                                        <ImageIcon aria-hidden size={12} className="shrink-0" />
                                        {picturesWord(r.change.reply.files.length)}
                                    </span>
                                )}
                                <Tag tone={done ? "green" : "blue"} className="mt-1">
                                    {done ? "Done" : r.kind === "call" && r.call.status === "scheduled" ? "Booked" : "In progress"}
                                </Tag>
                            </li>
                        );
                    })}
                </ul>
            )}
            {read === "ready" && total > 0 && (
                <p className="m-0 mt-3">
                    <ArrowLink to={`${OURA_ROOT}/work#your-requests`}>{total > shown.length ? `See all ${total} requests` : "See all requests"}</ArrowLink>
                </p>
            )}
        </div>
    );
}
